// A story or dialogue card: the illustration when one is set, otherwise a calm ornamental header with the
// Prakrit title over a palm-leaf rule; then the title in Hindi and English, the level, the length and a soft
// suggestion when the learner has not passed that unit's test yet. Stories awaiting review are muted and not links
// (worded as "opens soon": learners never see review wording).

import type { FC, ReactNode } from 'react'
import type { LessonRecord } from '@/prakrit/lib/progress-shared'
import type { StoryCardData } from '@/prakrit/components/stories/story-logic'

import Link from 'next/link'
import Image from 'next/image'
import {
    ArrowRight,
    BookOpenText,
    Eye,
    Hourglass,
    Lightbulb,
    MessagesSquare
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { PlanChip } from '@/components/plans/plan-notice'
import { PASS_MARK, UNITS } from '@/prakrit/lib/content/catalog'
import { useProgress } from '@/prakrit/lib/client/progress'
import { useReviewView } from '@/hooks/use-review-view'
import { StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'
import { hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { storyImageSrc } from '@/prakrit/components/stories/story-logic'

const unitTest = (unitNumber: number): string | null => UNITS
    .find(u => u.number === unitNumber)
    ?.lessons.find(l => l.kind === 'test')?.id ?? null

/**
 * Has the learner passed the test of this unit, or of any later unit? Stories never lock;
 * this only decides whether to suggest finishing the unit first.
 */
export const levelReached = (level: number, lessons: Record<string, LessonRecord>): boolean => {
    if (level <= 0) return true

    return UNITS.filter(u => u.number >= level).some(u => {
        const id = unitTest(u.number)
        const record = id ? lessons[id] : undefined

        return record?.status === 'completed' && record.best >= PASS_MARK
    })
}

/** "इकाई २ के बाद · after Unit 2" */
export const LevelLabel: FC<{ level: number, className?: string }> = ({ level, className }): ReactNode => (
    <span className={cn('inline-flex flex-wrap items-baseline gap-x-1.5', className)}>
        <span lang='hi' className='hindi'>इकाई {hiDigits(level)} के बाद</span>
        <span aria-hidden='true' className='ex-sep text-muted-foreground/70'>·</span>
        <span lang='en' data-ex='en' className='text-muted-foreground'>after Unit {level}</span>
    </span>
)

/** "सुझाव: पहले इकाई २ पूरी करें · Suggested after Unit 2" — shown only once progress has loaded. */
export const LevelSuggestion: FC<{ level: number, className?: string }> = ({ level, className }): ReactNode => {
    const { ready, snapshot } = useProgress()

    if (!ready || levelReached(level, snapshot.lessons)) return null

    return (
        <p className={cn('flex items-start gap-2 rounded-lg bg-saffron/10 px-3 py-2 text-sm', className)}>
            <Lightbulb className='mt-0.5 size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
            <span className='flex flex-col'>
                <span lang='hi' data-ex='hi' className='hindi'>सुझाव: पहले इकाई {hiDigits(level)} पूरी करें</span>
                <span lang='en' data-ex='en' className='text-muted-foreground'>Suggested after Unit {level}. You can still read it now.</span>
            </span>
        </p>
    )
}

const Ornament: FC<{ story: StoryCardData }> = ({ story }): ReactNode => (
    <div
        className={cn(
            'flex flex-col items-center gap-3 px-5 pb-4 pt-7 text-center',
            story.kind === 'dialogue' ? 'bg-terracotta/6' : 'bg-surface-muted/80',
            'in-data-[mode=kids]:bg-saffron/10'
        )}
    >
        <span lang='pra' className='pra pra-xl'>{story.title.pra}</span>
        <span aria-hidden='true' className='leaf-rule w-full max-w-60' />
    </div>
)

const Meta: FC<{ story: StoryCardData }> = ({ story }): ReactNode => (
    <ul className='flex flex-wrap gap-x-4 gap-y-1 text-sm'>
        <li>
            <LevelLabel level={story.level} />
        </li>
        <li className='inline-flex items-baseline gap-x-1.5'>
            <span lang='hi' className='hindi'>{hiDigits(story.lineCount)} पंक्तियाँ</span>
            <span aria-hidden='true' className='ex-sep text-muted-foreground/70'>·</span>
            <span lang='en' data-ex='en' className='text-muted-foreground'>{story.lineCount} lines</span>
        </li>
        <li className='inline-flex items-baseline gap-x-1.5'>
            <span lang='hi' className='hindi'>{hiDigits(story.questionCount)} प्रश्न</span>
            <span aria-hidden='true' className='ex-sep text-muted-foreground/70'>·</span>
            <span lang='en' data-ex='en' className='text-muted-foreground'>{story.questionCount} questions</span>
        </li>
    </ul>
)

const Speakers: FC<{ story: StoryCardData }> = ({ story }): ReactNode => {
    if (story.kind !== 'dialogue' || story.speakers.length === 0) return null

    return (
        <p className='flex flex-wrap items-center gap-1.5 text-sm'>
            <span lang='hi' className='hindi text-muted-foreground'>बोलने वाले:</span>
            {story.speakers.map(s => (
                <span key={`${s.hi}-${s.en}`} className='inline-flex items-baseline gap-1 rounded-full bg-secondary px-2.5 py-0.5'>
                    {s.pra ? <span lang='pra' className='font-prakrit font-semibold text-prakrit'>{s.pra}</span> : <span lang='hi' className='hindi font-semibold'>{s.hi}</span>}
                    <span lang='en' className='text-xs text-muted-foreground'>{s.en}</span>
                </span>
            ))}
        </p>
    )
}

interface StoryCardProps {
    story: StoryCardData
    className?: string
}

export const StoryCard: FC<StoryCardProps> = ({ story, className }): ReactNode => {
    const openable = story.visible || story.preview
    const reviewView = useReviewView()
    const KindIcon = story.kind === 'dialogue' ? MessagesSquare : BookOpenText
    const titleId = `story-${story.id}-title`

    const body = (
        <>
            {story.image ? (
                <Image
                    src={storyImageSrc(story.image)}
                    alt=''
                    width={1200}
                    height={800}
                    sizes='(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw'
                    className={cn('aspect-video h-auto w-full object-cover', !openable && 'opacity-60 grayscale')}
                />
            ) : (
                <Ornament story={story} />
            )}

            <div className='flex flex-1 flex-col gap-3 p-5'>
                <p className='flex flex-wrap items-center gap-2 text-xs font-semibold tracking-wide text-saffron-strong'>
                    <KindIcon className='size-4' aria-hidden='true' />
                    <span lang='hi' className='hindi'>{story.kind === 'dialogue' ? 'बातचीत' : 'कथा'}</span>
                    <span lang='en' className='font-medium text-muted-foreground'>{story.kind === 'dialogue' ? 'Dialogue' : 'Story'}</span>
                    {story.visible && story.status !== 'published' && <StatusBadge kind={reviewBadgeKind(story.status)} className='ml-auto' />}
                    {story.locked && <PlanChip className={cn(!(story.visible && story.status !== 'published') && 'ml-auto')} />}
                </p>

                <h3 id={titleId} className='flex flex-col gap-0.5'>
                    {story.image && <span lang='pra' className='pra pra-lg'>{story.title.pra}</span>}
                    <span lang='hi' className='hindi text-lg font-semibold leading-snug text-foreground'>{story.title.hi}</span>
                    <span lang='en' className='font-sans text-base font-medium text-muted-foreground'>{story.title.en}</span>
                </h3>

                <Meta story={story} />
                <Speakers story={story} />

                {story.teachingPoint && openable && (
                    <p className='adults-only text-sm leading-relaxed'>
                        <span lang='hi' data-ex='hi' className='hindi block'>{story.teachingPoint.hi}</span>
                        <span lang='en' data-ex='en' className='block text-muted-foreground'>{story.teachingPoint.en}</span>
                    </p>
                )}

                {openable && <LevelSuggestion level={story.level} />}

                {openable ? (
                    <span className='mt-auto flex flex-wrap items-center justify-between gap-2 pt-1'>
                        {story.preview && reviewView ? (
                            <span className='inline-flex items-center gap-1.5 rounded-full border border-saffron/40 bg-saffron/10 px-2.5 py-1 text-xs font-semibold text-saffron-strong'>
                                <Eye className='size-3.5' aria-hidden='true' />
                                <span lang='hi' className='hindi'>समीक्षा पूर्वावलोकन</span>
                                <span lang='en' className='font-medium'>Reviewer preview</span>
                            </span>
                        ) : <span aria-hidden='true' />}
                        <span className='inline-flex items-center gap-1.5 text-sm font-semibold text-prakrit'>
                            <span lang='hi' className='hindi adults-only'>पढ़ें</span>
                            <span lang='hi' className='hindi kids-only'>चलो पढ़ें!</span>
                            <span aria-hidden='true' className='text-muted-foreground'>·</span>
                            <span lang='en'>Read</span>
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </span>
                    </span>
                ) : (
                    <p className='mt-auto flex items-start gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground'>
                        <Hourglass className='mt-0.5 size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
                        <span className='flex flex-col'>
                            <span lang='hi' className='hindi text-foreground/80'>जल्द खुलेगी</span>
                            <span lang='en'>Opens soon.</span>
                        </span>
                    </p>
                )}
            </div>
        </>
    )

    const frame = cn(
        'flex h-full flex-col overflow-hidden rounded-xl border bg-card shadow-card',
        'in-data-[mode=kids]:border-2',
        className
    )

    if (!openable) {
        return (
            <article aria-labelledby={titleId} className={cn(frame, 'border-dashed border-border bg-card/60 shadow-none')}>
                {body}
            </article>
        )
    }

    return (
        <Link
            href={`/stories/${encodeURIComponent(story.id)}`}
            aria-labelledby={titleId}
            className={cn(
                frame,
                'group border-border transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-saffron/60 motion-reduce:hover:translate-y-0'
            )}
        >
            {body}
        </Link>
    )
}
